import { useState, useEffect } from 'react';
import {
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  ChevronDown,
  Save,
  X,
  Eye,
  EyeOff,
  Utensils,
  Droplet,
  Car,
  Ticket,
  ShieldAlert,
  Package,
  Info,
  Users,
  Flag,
  Navigation,
  Coffee,
  Loader2,
  Upload,
  MapPinned,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';

// Available icons for POIs
const AVAILABLE_ICONS = {
  utensils: Utensils,
  droplet: Droplet,
  car: Car,
  ticket: Ticket,
  shield: ShieldAlert,
  package: Package,
  info: Info,
  users: Users,
  flag: Flag,
  navigation: Navigation,
  coffee: Coffee,
  mappin: MapPin,
};

// POI Categories
const POI_CATEGORIES = {
  food: { icon: Utensils, label: 'Food & Drinks', color: 'bg-orange-500' },
  restrooms: { icon: Droplet, label: 'Restrooms', color: 'bg-blue-500' },
  parking: { icon: Car, label: 'Parking', color: 'bg-gray-500' },
  tickets: { icon: Ticket, label: 'Tickets', color: 'bg-purple-500' },
  medical: { icon: ShieldAlert, label: 'First Aid', color: 'bg-red-500' },
  merchandise: { icon: Package, label: 'Merchandise', color: 'bg-pink-500' },
  info: { icon: Info, label: 'Information', color: 'bg-green-500' },
  vip: { icon: Users, label: 'VIP Areas', color: 'bg-yellow-500' },
  grandstand: { icon: Flag, label: 'Grandstands', color: 'bg-indigo-500' },
};

type POICategory = keyof typeof POI_CATEGORIES;

interface POI {
  id: string;
  name: string;
  category: POICategory;
  description: string;
  map_x: number;
  map_y: number;
  latitude: number | null;
  longitude: number | null;
  floor: string | null;
  icon_name: string | null;
  custom_icon_url: string | null;
  is_open: number;
  wait_time: string | null;
  status: 'active' | 'inactive';
}

// Track map image
const TRACK_MAP = '/map.png';

export function AdminPOIs() {
  const [pois, setPOIs] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<POICategory | 'all'>('all');
  const [editingPOI, setEditingPOI] = useState<POI | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [placingPOI, setPlacingPOI] = useState(false);
  const { addToast } = useUIStore();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'food' as POICategory,
    description: '',
    mapX: 50,
    mapY: 50,
    latitude: '',
    longitude: '',
    floor: '',
    iconName: '',
    customIconUrl: '',
    isOpen: true,
    waitTime: '',
    status: 'active' as 'active' | 'inactive',
  });

  // Fetch POIs from API
  useEffect(() => {
    fetchPOIs();
  }, []);

  const fetchPOIs = async () => {
    setLoading(true);
    try {
      const response = await api.get<POI[]>('/api/admin/pois');
      if (response.success && response.data) {
        setPOIs(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch POIs:', error);
      addToast('error', 'Failed to load POIs');
    } finally {
      setLoading(false);
    }
  };

  const filteredPOIs = pois.filter((poi) => {
    const matchesSearch =
      searchQuery === '' ||
      poi.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (poi.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || poi.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleEdit = (poi: POI) => {
    setEditingPOI(poi);
    setFormData({
      name: poi.name,
      category: poi.category,
      description: poi.description || '',
      mapX: poi.map_x,
      mapY: poi.map_y,
      latitude: poi.latitude?.toString() || '',
      longitude: poi.longitude?.toString() || '',
      floor: poi.floor || '',
      iconName: poi.icon_name || '',
      customIconUrl: poi.custom_icon_url || '',
      isOpen: poi.is_open === 1,
      waitTime: poi.wait_time || '',
      status: poi.status,
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setEditingPOI(null);
    setFormData({
      name: '',
      category: 'food',
      description: '',
      mapX: 50,
      mapY: 50,
      latitude: '',
      longitude: '',
      floor: '',
      iconName: '',
      customIconUrl: '',
      isOpen: true,
      waitTime: '',
      status: 'active',
    });
    setIsCreating(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      addToast('error', 'Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        description: formData.description || null,
        mapX: formData.mapX,
        mapY: formData.mapY,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        floor: formData.floor || null,
        iconName: formData.iconName || null,
        customIconUrl: formData.customIconUrl || null,
        isOpen: formData.isOpen,
        waitTime: formData.waitTime || null,
        status: formData.status,
      };

      if (isCreating) {
        const response = await api.post('/api/admin/pois', payload);
        if (response.success) {
          addToast('success', 'POI created successfully');
          fetchPOIs();
        } else {
          addToast('error', response.error || 'Failed to create POI');
        }
      } else if (editingPOI) {
        const response = await api.patch(`/api/admin/pois/${editingPOI.id}`, payload);
        if (response.success) {
          addToast('success', 'POI updated successfully');
          fetchPOIs();
        } else {
          addToast('error', response.error || 'Failed to update POI');
        }
      }
      setEditingPOI(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Save error:', error);
      addToast('error', 'Failed to save POI');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this POI?')) return;

    try {
      const response = await api.delete(`/api/admin/pois/${id}`);
      if (response.success) {
        addToast('success', 'POI deleted successfully');
        fetchPOIs();
        if (editingPOI?.id === id) {
          setEditingPOI(null);
        }
      } else {
        addToast('error', response.error || 'Failed to delete POI');
      }
    } catch (error) {
      console.error('Delete error:', error);
      addToast('error', 'Failed to delete POI');
    }
  };

  const handleToggleStatus = async (poi: POI) => {
    const newStatus = poi.status === 'active' ? 'inactive' : 'active';
    try {
      const response = await api.patch(`/api/admin/pois/${poi.id}`, { status: newStatus });
      if (response.success) {
        addToast('success', `POI ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchPOIs();
      }
    } catch (error) {
      addToast('error', 'Failed to update status');
    }
  };

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placingPOI) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);

    setFormData({ ...formData, mapX: x, mapY: y });
    setPlacingPOI(false);
  };

  const getIconComponent = (poi: POI) => {
    if (poi.icon_name && AVAILABLE_ICONS[poi.icon_name as keyof typeof AVAILABLE_ICONS]) {
      return AVAILABLE_ICONS[poi.icon_name as keyof typeof AVAILABLE_ICONS];
    }
    return POI_CATEGORIES[poi.category]?.icon || MapPin;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Venue POIs</h1>
          <p className="text-gray-400">Manage points of interest with GPS coordinates</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowMap(!showMap)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showMap
                ? 'border-red-600 bg-red-600/20 text-red-400'
                : 'border-gray-600 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <MapPin size={18} />
            {showMap ? 'Hide Map' : 'Show Map'}
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Plus size={18} />
            Add POI
          </button>
        </div>
      </div>

      {/* Map View */}
      {showMap && (
        <div className="card bg-gray-800 border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">Map View</h2>
            <span className="text-sm text-gray-400">
              {filteredPOIs.filter((p) => p.status === 'active').length} active POIs
            </span>
          </div>
          <div
            className={`relative aspect-[16/9] bg-gray-900 rounded-xl overflow-hidden ${
              placingPOI ? 'cursor-crosshair' : 'cursor-default'
            }`}
            onClick={handleMapClick}
          >
            <img src={TRACK_MAP} alt="Track Map" className="w-full h-full object-contain bg-white" />

            {/* POI Markers */}
            {filteredPOIs.map((poi) => {
              const Icon = getIconComponent(poi);
              const color = POI_CATEGORIES[poi.category]?.color || 'bg-gray-500';
              return (
                <button
                  key={poi.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(poi);
                  }}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all hover:scale-110 z-10 ${
                    poi.status === 'inactive' ? 'opacity-50' : ''
                  } ${editingPOI?.id === poi.id ? 'ring-4 ring-white scale-125 z-20' : ''}`}
                  style={{ left: `${poi.map_x}%`, top: `${poi.map_y}%` }}
                  title={poi.name}
                >
                  {poi.custom_icon_url ? (
                    <img
                      src={poi.custom_icon_url}
                      alt={poi.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-lg"
                    />
                  ) : (
                    <div className={`w-8 h-8 ${color} rounded-full flex items-center justify-center text-white shadow-lg`}>
                      <Icon size={16} />
                    </div>
                  )}
                </button>
              );
            })}

            {/* Placement marker */}
            {(isCreating || editingPOI) && (
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20 animate-pulse pointer-events-none"
                style={{ left: `${formData.mapX}%`, top: `${formData.mapY}%` }}
              >
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg ring-4 ring-red-300">
                  <MapPin size={20} />
                </div>
              </div>
            )}

            {placingPOI && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <p className="px-4 py-2 bg-white rounded-lg shadow-lg font-medium text-gray-900">
                  Click on the map to place POI
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search POIs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        <div className="relative">
          <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as POICategory | 'all')}
            className="pl-10 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-red-500 focus:border-red-500 appearance-none"
          >
            <option value="all">All Categories</option>
            {(Object.entries(POI_CATEGORIES) as [POICategory, typeof POI_CATEGORIES.food][]).map(
              ([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              )
            )}
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* POI List */}
        <div className="lg:col-span-2">
          <div className="card bg-gray-800 border-gray-700 divide-y divide-gray-700">
            {filteredPOIs.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                No POIs found. Add your first point of interest.
              </div>
            ) : (
              filteredPOIs.map((poi) => {
                const Icon = getIconComponent(poi);
                const { color, label } = POI_CATEGORIES[poi.category] || { color: 'bg-gray-500', label: poi.category };
                return (
                  <div
                    key={poi.id}
                    className={`p-4 flex items-center gap-4 ${
                      poi.status === 'inactive' ? 'opacity-60' : ''
                    } ${editingPOI?.id === poi.id ? 'bg-gray-700/50' : ''}`}
                  >
                    {poi.custom_icon_url ? (
                      <img
                        src={poi.custom_icon_url}
                        alt={poi.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white`}>
                        <Icon size={18} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-white truncate">{poi.name}</h3>
                        {poi.status === 'inactive' && (
                          <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 truncate">{poi.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{label}</span>
                        <span>• Map: ({poi.map_x}%, {poi.map_y}%)</span>
                        {poi.latitude && poi.longitude && (
                          <span className="text-green-400">
                            <MapPinned size={12} className="inline mr-1" />
                            GPS
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(poi)}
                        className="p-2 text-gray-400 hover:text-white transition-colors"
                        title={poi.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        {poi.status === 'active' ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        onClick={() => handleEdit(poi)}
                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(poi.id)}
                        className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Edit/Create Form */}
        <div className="lg:col-span-1">
          {(isCreating || editingPOI) && (
            <div className="card bg-gray-800 border-gray-700 p-6 sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">
                  {isCreating ? 'Create POI' : 'Edit POI'}
                </h2>
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setEditingPOI(null);
                  }}
                  className="p-1 text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                    placeholder="e.g., Main Concession Stand"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as POICategory })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-red-500 focus:border-red-500"
                  >
                    {(Object.entries(POI_CATEGORIES) as [POICategory, typeof POI_CATEGORIES.food][]).map(
                      ([key, { label }]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                    placeholder="Brief description..."
                  />
                </div>

                {/* Map Position */}
                <div className="p-3 bg-gray-700/50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <MapPin size={14} className="inline mr-1" />
                    Map Position
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">X (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.mapX}
                        onChange={(e) => setFormData({ ...formData, mapX: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Y (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.mapY}
                        onChange={(e) => setFormData({ ...formData, mapY: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                  {showMap && (
                    <button
                      onClick={() => setPlacingPOI(true)}
                      className="w-full px-3 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <Navigation size={14} className="inline mr-2" />
                      Click to Place on Map
                    </button>
                  )}
                </div>

                {/* GPS Coordinates */}
                <div className="p-3 bg-gray-700/50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <MapPinned size={14} className="inline mr-1" />
                    GPS Coordinates (for navigation)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Latitude</label>
                      <input
                        type="text"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                        placeholder="-19.2590"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Longitude</label>
                      <input
                        type="text"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                        placeholder="146.8169"
                      />
                    </div>
                  </div>
                </div>

                {/* Icon Selection */}
                <div className="p-3 bg-gray-700/50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Custom Icon
                  </label>
                  <div className="mb-2">
                    <label className="block text-xs text-gray-400 mb-1">Select Icon</label>
                    <select
                      value={formData.iconName}
                      onChange={(e) => setFormData({ ...formData, iconName: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Default (by category)</option>
                      {Object.keys(AVAILABLE_ICONS).map((iconKey) => (
                        <option key={iconKey} value={iconKey}>
                          {iconKey.charAt(0).toUpperCase() + iconKey.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Or Custom Icon URL</label>
                    <input
                      type="text"
                      value={formData.customIconUrl}
                      onChange={(e) => setFormData({ ...formData, customIconUrl: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Floor/Level</label>
                  <input
                    type="text"
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                    placeholder="e.g., Level 1, Ground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Wait Time</label>
                  <input
                    type="text"
                    value={formData.waitTime}
                    onChange={(e) => setFormData({ ...formData, waitTime: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-red-500 focus:border-red-500"
                    placeholder="e.g., 5-10 min"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isOpen}
                      onChange={(e) => setFormData({ ...formData, isOpen: e.target.checked })}
                      className="rounded border-gray-600 bg-gray-700 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-300">Currently Open</span>
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={handleSave}
                    disabled={!formData.name || saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Save size={18} />
                    )}
                    {isCreating ? 'Create POI' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!isCreating && !editingPOI && (
            <div className="card bg-gray-800 border-gray-700 p-6 text-center text-gray-400">
              <MapPin size={48} className="mx-auto mb-4 text-gray-600" />
              <p>Select a POI to edit or create a new one.</p>
              <button
                onClick={handleCreate}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Plus size={18} />
                Add New POI
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
